import { useState } from 'react'
import type { Ref } from 'react'
import type { ButtonProps } from './Button.types'
import { ShortcutBadge } from './ShortcutBadge'
import './Button.scss'

/**
 * Button Component
 *
 * Supports two variants (primary/secondary) and flexible keyboard shortcut badges.
 *
 * @example
 * // Primary button with return shortcut
 * <Button shortcut={{ variant: 'return' }}>Continue</Button>
 *
 * @example
 * // Secondary button with ESC shortcut
 * <Button variant="secondary" shortcut={{ variant: 'character', text: 'ESC' }}>Cancel</Button>
 *
 * @example
 * // Button with CMD modifier + key (shows ⌘ S as two separate badges)
 * <Button shortcut={{ variant: 'character', text: 'S', cmd: true }}>Save</Button>
 *
 * @example
 * // Button with Ctrl modifier + key (shows Ctrl/Strg Enter as two separate badges)
 * <Button shortcut={{ variant: 'return', ctrl: true }}>Submit</Button>
 *
 * @example
 * // Array of shortcuts (e.g. multiple keys without modifier)
 * <Button shortcut={[
 *   { variant: 'character', text: 'G' },
 *   { variant: 'character', text: 'H' }
 * ]}>
 *   Go Home
 * </Button>
 *
 * @example
 * // Button with add icon shortcut
 * <Button shortcut={{ variant: 'add' }}>Create New</Button>
 */

export function Button({
  onClick,
  children,
  variant = 'primary',
  size = 'default',
  shortcut,
  icon,
  disabled = false,
  type = 'button',
  style,
  className,
  ref,
  ...rest
}: ButtonProps & { ref?: Ref<HTMLButtonElement> }) {
  const [isPressed, setIsPressed] = useState(false)

  // Support 'style' prop as alias for 'variant' (backwards compatibility)
  const finalVariant = (style as ButtonProps['variant']) || variant

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === 'Enter' || e.code === 'Enter') && !e.repeat) {
      e.preventDefault()
      setIsPressed(true)
      if (onClick) {
        onClick()
      }
      // Reset nach kurzer Zeit
      setTimeout(() => setIsPressed(false), 200)
    }
  }

  const handleKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      setIsPressed(false)
    }
  }

  const buttonClass = `button button--${finalVariant} button--size-${size} ${isPressed ? 'button--pressed' : ''} ${className || ''}`.trim()

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      className={buttonClass}
      disabled={disabled}
    >
      {icon && <span className="button__icon">{icon}</span>}
      {children && <span className="button__text">{children}</span>}
      {shortcut && (
        <div className="button__icon-wrapper">
          <ShortcutBadge shortcut={shortcut} variant={finalVariant} />
        </div>
      )}
    </button>
  )
}
